import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import intl from "react-intl-universal";
import { MdClose } from "react-icons/md";
import { TbLock, TbLockCheck } from "react-icons/tb";
import { PasswordConfirm } from "../../../Components/Molecules";
import { useAccountData } from "../../../Hooks";
import { useToast } from "../../../Hooks/toast";
import {
  changeTransactionalPassword,
  registerTransactionalPassword,
} from "../../../Hooks/UsePix";

const MODAL_PAPER_MOBILE =
  "!flex !flex-col !h-full !overflow-x-hidden !shadow-card app-card";
const MODAL_PAPER_DESKTOP =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !max-h-[90vh] !w-full !max-w-[calc(100vw-2rem)] !overflow-x-hidden sm:!max-w-md";

/**
 * Settings → Security → Senha transacional (modal)
 * - Not qualified: digit pad ×2 → POST
 * - Qualified: pair keypad (current) → digit pad ×2 (new) → PUT
 */
export const TransactionalPasswordModal = ({ open, onClose, onSuccess, autoStartRegister = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const toast = useToast();
  const passwordRef = useRef(null);
  const {
    state: accountData,
    handleGET: refreshAccount,
    loading: accountLoading,
  } = useAccountData();

  const [flow, setFlow] = useState(null); // null | "register" | "change-current" | "change-new"
  const [currentCombination, setCurrentCombination] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isAccountLoading = Boolean(accountLoading?.GET);
  const isQualified = accountData?.transactionalPasswordQualified === true;

  useEffect(() => {
    if (!open) {
      setFlow(null);
      setCurrentCombination(null);
      setError(null);
      setSubmitting(false);
      return;
    }
    refreshAccount?.({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh when modal opens
  }, [open]);

  useEffect(() => {
    if (!open || isAccountLoading || isQualified || !autoStartRegister) return;
    if (flow) return;
    setError(null);
    setFlow("register");
  }, [open, isAccountLoading, isQualified, autoStartRegister, flow]);

  const resetFlow = useCallback(() => {
    setFlow(null);
    setCurrentCombination(null);
    setError(null);
    setSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetFlow();
    onClose?.();
  }, [submitting, resetFlow, onClose]);

  const handleSuccess = useCallback(
    async (messageKey) => {
      await refreshAccount?.({});
      onSuccess?.();
      toast.addToast({
        type: "success",
        title: intl.get(messageKey),
      });
      resetFlow();
      onClose?.();
    },
    [refreshAccount, onSuccess, toast, resetFlow, onClose]
  );

  const handleSubmit = useCallback(
    async (payload) => {
      setError(null);

      if (flow === "register") {
        const { password, confirmPassword } = payload || {};
        setSubmitting(true);
        const result = await registerTransactionalPassword({ password, confirmPassword });
        setSubmitting(false);

        if (!result.success) {
          setError({
            title: result.message || intl.get("passwordConfirm.warning"),
            description: result.description,
          });
          return;
        }

        await handleSuccess("profile.transactionalPasswordRegistered");
        return;
      }

      if (flow === "change-current") {
        setCurrentCombination(payload);
        setFlow("change-new");
        return;
      }

      if (flow === "change-new") {
        const { password, confirmPassword } = payload || {};
        setSubmitting(true);
        const result = await changeTransactionalPassword({
          passwordCombination: currentCombination,
          password,
          confirmPassword,
        });
        setSubmitting(false);

        if (!result.success) {
          const isCurrentWrong = result.error === "invalid_current_password";
          if (isCurrentWrong) {
            setCurrentCombination(null);
            setFlow("change-current");
          }
          setError({
            title: result.message || intl.get("passwordConfirm.warning"),
            description: result.description,
          });
          return;
        }

        await handleSuccess("profile.transactionalPasswordChanged");
      }
    },
    [flow, currentCombination, handleSuccess]
  );

  const startRegister = () => {
    setError(null);
    setFlow("register");
  };

  const startChange = () => {
    setError(null);
    setCurrentCombination(null);
    setFlow("change-current");
  };

  const statusLabel = isQualified
    ? intl.get("profile.transactionalPasswordActive")
    : intl.get("profile.transactionalPasswordNotSet");

  const statusTone = isQualified
    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
    : "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800";

  const StatusIcon = isQualified ? TbLockCheck : TbLock;

  return (
    <Dialog
      className="transactional-password-modal"
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        className: isMobile ? MODAL_PAPER_MOBILE : MODAL_PAPER_DESKTOP,
      }}
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800 sm:px-6">
          <h2 className="m-0 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
            <TbLock className="h-5 w-5 text-[#e7a73f]" />
            {intl.get("profile.transactionalPassword")}
          </h2>
          <button
            type="button"
            aria-label={intl.get("words.close")}
            onClick={handleClose}
            disabled={submitting}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-gray-800 dark:hover:text-gray-100"
          >
            <MdClose className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("profile.transactionalPasswordSubtitle")}
          </p>

          <div className="overflow-hidden rounded-xl border border-gray-100 dark:border-brand-dark-border">
            <div className="flex items-start gap-3 border-b border-gray-100 px-4 py-4 dark:border-brand-dark-border sm:items-center">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-brand-cream/60 dark:border-brand-dark-border dark:bg-[#1f1f1f]">
                <StatusIcon className="h-5 w-5 text-brand-dark dark:text-brand-gold-mid" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                  {intl.get("profile.transactionalPassword")}
                </p>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {intl.get("profile.transactionalPasswordHint")}
                </p>
              </div>
              <span
                className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${statusTone}`}
              >
                {isAccountLoading ? "…" : statusLabel}
              </span>
            </div>

            {!flow && !isAccountLoading && (
              <div className="flex flex-col gap-2 px-4 py-4 sm:flex-row sm:justify-end">
                {isQualified ? (
                  <button type="button" className="btn-primary w-full sm:w-auto" onClick={startChange}>
                    {intl.get("profile.changeTransactionalPassword")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-primary w-full sm:w-auto"
                    onClick={startRegister}
                  >
                    {intl.get("profile.registerTransactionalPassword")}
                  </button>
                )}
              </div>
            )}
          </div>

          {flow ? (
            <div className="mt-4">
              <PasswordConfirm
                key={flow}
                ref={passwordRef}
                variant="inline"
                mode={flow === "register" || flow === "change-new" ? "register" : "confirm"}
                title={
                  flow === "change-current"
                    ? intl.get("profile.currentTransactionalPasswordTitle")
                    : undefined
                }
                subtitle={
                  flow === "change-current"
                    ? intl.get("profile.currentTransactionalPasswordHint")
                    : undefined
                }
                submitPassword={handleSubmit}
                onBack={() => {
                  if (flow === "change-new") {
                    setCurrentCombination(null);
                    setError(null);
                    setFlow("change-current");
                    return;
                  }
                  resetFlow();
                }}
                error={error}
                loading={submitting}
                onDismissError={() => setError(null)}
              />
            </div>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
};

export default TransactionalPasswordModal;
